import * as React from 'react';

import classnames from 'classnames';

import {Card} from 'shells/card/card';
import {colors} from 'shells/colors/colors';
import {CheckCircleRegularIcon} from 'shells/icon/svg/check-circle-regular-icon';
import {CheckCircleSolidIcon} from 'shells/icon/svg/check-circle-solid-icon';
import {Microsoft365RegularIcon} from 'shells/icon/svg/microsoft-365-regular-icon';
import {Stack} from 'shells/layout/stack/stack';
import GoogleLogoSvg from 'shells/logos/google-g.svg';
import {Body} from 'shells/typography/body';

import type {
    OnboardingStepFragment,
    UserEmailCalendarStatusesFragment,
    OnboardingQuotesFragment,
    UserVideoIntegrationsFragment,
} from 'nutshell-graphql-types';

import {NewBroadcastModal} from '../../../email-marketing/broadcasts/new-broadcast-modal';
import {OpenableModals} from '../../../openable-modals/openable-modals';
import {useOpenableModal} from '../../../openable-modals/use-openable-modal';
import {CreatePipelineModal} from '../../../sales-automation/home/create-pipeline-modal';
import {CreateMeetingModal} from '../../../scheduler/create-modals/multi-host/create-meeting-modal';
import {useSetGoogleMailSendIsEnabled} from '../../../setup/email/graphql/hooks/use-set-google-mail-send-is-enabled';
import {useSetOffice365MailSendIsEnabled} from '../../../setup/email/graphql/hooks/use-set-office365-mail-send-is-enabled';
import {InitialButton} from '../../../setup/integrations/initial-button';

import {analyticsClient} from '../../../../analytics-client';
import {appHistory} from '../../../../history';

import type {ExplorePageVariant} from '../types';

import {ConnectMeetingModal} from './connect-meeting-modal';
import {getOnboardingStepClickHandler, getStepDisabledTooltip} from './helpers';

type Props = {
    step: OnboardingStepFragment;
    hasFullSchedulerAccess: boolean;
    isAdmin: boolean;
    userEmailCalendarStatuses: UserEmailCalendarStatusesFragment | null | undefined;
    onboardingQuotes: OnboardingQuotesFragment | null | undefined;
    userVideoIntegrations: UserVideoIntegrationsFragment | null | undefined;
    hasClickToCallConnected?: boolean;
    hasPhoneIntegrationConnected?: boolean;
    variant: ExplorePageVariant;
    onEmailCalendarAuthSuccess?: (provider: 'google' | 'microsoft') => void;
};

function isEmailCalendarSyncStep(stepType: string): boolean {
    return stepType === 'EMAIL_SYNC' || stepType === 'EMAIL_CALENDAR_SYNC';
}

export function OnboardingStep(props: Props) {
    const [isCreateSchedulerMeetingModalOpen, setIsCreateSchedulerMeetingModalOpen] =
        React.useState<boolean>(false);
    const [isAddingNewBroadcast, setIsAddingNewBroadcast] = React.useState<boolean>(false);
    const [isCreatingPipeline, setIsCreatingPipeline] = React.useState<boolean>(false);
    const [isConnectMeetingModalOpen, setIsConnectMeetingModalOpen] =
        React.useState<boolean>(false);
    const [authErrorMessages, setAuthErrorMessages] = React.useState<string[]>([]);

    const {onOpen: onOpenInviteUsersModal} = useOpenableModal(OpenableModals.INVITE_USERS_MODAL);
    const {onOpen: onOpenImportModal} = useOpenableModal(OpenableModals.IMPORT_MODAL);

    const {enableGoogle} = useSetGoogleMailSendIsEnabled();
    const {enableO365} = useSetOffice365MailSendIsEnabled();

    const onClickHandler = React.useMemo(
        () =>
            getOnboardingStepClickHandler(
                props.step,
                props.userEmailCalendarStatuses,
                props.userVideoIntegrations,
                appHistory,
                onOpenInviteUsersModal,
                onOpenImportModal,
                setIsCreateSchedulerMeetingModalOpen,
                setIsAddingNewBroadcast,
                setIsCreatingPipeline,
                setIsConnectMeetingModalOpen,
                props.onboardingQuotes,
                props.hasPhoneIntegrationConnected
            ),
        [
            props.step,
            props.userEmailCalendarStatuses,
            props.userVideoIntegrations,
            props.onboardingQuotes,
            props.hasPhoneIntegrationConnected,
            onOpenInviteUsersModal,
            onOpenImportModal,
        ]
    );

    const handleClick = React.useCallback(() => {
        if (onClickHandler) {
            analyticsClient.track(analyticsClient.EVENTS.TRACK_ONBOARDING_STEP_STARTED, {
                type: props.step.type,
                variant: props.variant,
            });

            onClickHandler();
        }
    }, [onClickHandler, props.step.type, props.variant]);

    const disabledTooltip = getStepDisabledTooltip(
        props.step,
        props.onboardingQuotes,
        Boolean(props.hasClickToCallConnected || props.hasPhoneIntegrationConnected)
    );
    const isDisabled = Boolean(disabledTooltip);
    const containerClassName = classnames({disabled: isDisabled});

    const showInlineAuthButtons =
        isEmailCalendarSyncStep(props.step.type) &&
        !props.step.isComplete &&
        props.userEmailCalendarStatuses &&
        props.userEmailCalendarStatuses.sendableEmailAddresses.length === 0 &&
        !props.userEmailCalendarStatuses.calendarSync;

    return (
        <>
            <div data-tip={disabledTooltip} className={containerClassName}>
                <Card
                    hasBorder={true}
                    borderRadius={8}
                    onClick={
                        !isDisabled && onClickHandler && !showInlineAuthButtons
                            ? handleClick
                            : undefined
                    }
                    borderColor={props.step.isComplete ? colors.greenDk : undefined}
                    fitHeight={true}
                    backgroundColor={props.step.isComplete ? 'successBg' : undefined}
                    hoverBackgroundColor={
                        props.step.isComplete && onClickHandler ? 'greenXlt' : undefined
                    }
                    hoverBorderColor={
                        props.step.isComplete && onClickHandler ? 'leadHovered' : undefined
                    }
                >
                    <div className='flex align-center gap-16 px-16 py-12 border-box'>
                        <div className='flex align-center'>
                            {props.step.isComplete ? (
                                <CheckCircleSolidIcon
                                    size={24}
                                    wrapWithDiv={false}
                                    // @ts-expect-error
                                    color={colors.greenDk}
                                />
                            ) : (
                                <CheckCircleRegularIcon
                                    size={24}
                                    wrapWithDiv={false}
                                    // @ts-expect-error
                                    color={colors.greyXlt}
                                />
                            )}
                        </div>
                        <Stack spacing={8}>
                            <Body>{props.step.title}</Body>
                            {showInlineAuthButtons && (
                                <Stack isHorizontal={true} spacing={8}>
                                    <InitialButton
                                        initialText='Google'
                                        authCheckUrl='/google/has-auth'
                                        windowFeatures={{
                                            toolbar: 'no',
                                            location: 'no',
                                            width: 600,
                                            height: 850,
                                        }}
                                        logo={<GoogleLogoSvg width={20} />}
                                        enable={(args: Parameters<typeof enableGoogle>[0]) => {
                                            setAuthErrorMessages([]);
                                            return enableGoogle(args);
                                        }}
                                        onAuthSuccess={() => {
                                            props.onEmailCalendarAuthSuccess!('google');

                                            return Promise.resolve();
                                        }}
                                        onAuthError={(errMsgs) => setAuthErrorMessages(errMsgs)}
                                        centerContent={true}
                                        compact={true}
                                        borderVariant='feature'
                                    />
                                    <InitialButton
                                        initialText='Microsoft 365'
                                        authCheckUrl='/office365/has-auth'
                                        windowFeatures={{
                                            toolbar: 'no',
                                            location: 'no',
                                            width: 600,
                                            height: 500,
                                        }}
                                        logo={
                                            <Microsoft365RegularIcon
                                                size={20}
                                                wrapWithDiv={false}
                                            />
                                        }
                                        enable={(args: Parameters<typeof enableO365>[0]) => {
                                            setAuthErrorMessages([]);
                                            return enableO365(args);
                                        }}
                                        onAuthSuccess={() => {
                                            props.onEmailCalendarAuthSuccess!('microsoft');

                                            return Promise.resolve();
                                        }}
                                        onAuthError={(errMsgs) => setAuthErrorMessages(errMsgs)}
                                        centerContent={true}
                                        compact={true}
                                        borderVariant='feature'
                                    />
                                </Stack>
                            )}
                            {authErrorMessages.length > 0 && (
                                <Body color='error' size='small'>
                                    {authErrorMessages.join(', ')}
                                </Body>
                            )}
                        </Stack>
                    </div>
                </Card>
            </div>
            {isCreateSchedulerMeetingModalOpen && (
                <CreateMeetingModal
                    onClose={() => setIsCreateSchedulerMeetingModalOpen(false)}
                    hasFullSchedulerAccess={props.hasFullSchedulerAccess}
                    isAdmin={props.isAdmin}
                />
            )}
            {isAddingNewBroadcast && (
                <NewBroadcastModal
                    isOpen={isAddingNewBroadcast}
                    onClose={() => {
                        setIsAddingNewBroadcast(false);
                    }}
                    initialSelectedCategoryOrTemplateApiId='ai-email-template'
                />
            )}
            {isCreatingPipeline && (
                <CreatePipelineModal
                    isOpen={isCreatingPipeline}
                    onClose={() => {
                        setIsCreatingPipeline(false);
                    }}
                />
            )}
            {isConnectMeetingModalOpen && (
                <ConnectMeetingModal onClose={() => setIsConnectMeetingModalOpen(false)} />
            )}
        </>
    );
}
